class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.observeCustomilyRedirectOpen();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.removeAttribute('role');
    cartLink.removeAttribute('aria-haspopup');
  }

  isVisibleElement(element) {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  }

  getProductMediaToLock() {
    return Array.from(
      document.querySelectorAll('.product__media-wrapper .rio-media-gallery, .product__media-wrapper > #tee-gallery')
    ).find((element) => this.isVisibleElement(element));
  }

  lockProductMediaPosition() {
    const mediaElement = this.getProductMediaToLock();
    if (!mediaElement) return;

    const rect = mediaElement.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const placeholder = mediaElement.closest('.product__media-wrapper');
    this.unlockProductMediaPosition();
    this.lockedProductMedia = {
      element: mediaElement,
      style: mediaElement.getAttribute('style'),
      placeholder,
      placeholderStyle: placeholder?.getAttribute('style') ?? null,
    };

    if (placeholder) {
      placeholder.style.setProperty('min-height', `${rect.height}px`, 'important');
    }

    mediaElement.style.setProperty('position', 'fixed', 'important');
    mediaElement.style.setProperty('top', `${rect.top}px`, 'important');
    mediaElement.style.setProperty('left', `${rect.left}px`, 'important');
    mediaElement.style.setProperty('width', `${rect.width}px`, 'important');
    mediaElement.style.setProperty('height', `${rect.height}px`, 'important');
    mediaElement.style.setProperty('max-height', `${rect.height}px`, 'important');
  }

  shouldRedirectToCartAfterCustomilyAdd() {
    return Number(window.dmCustomilyCartRedirectUntil || 0) > Date.now();
  }

  redirectToCartAfterCustomilyAdd() {
    window.dmCustomilyCartRedirectUntil = 0;
    this.classList.remove('active');
    document.documentElement.classList.remove('cart-drawer-open');
    document.body.classList.remove('overflow-hidden');
    window.location.assign(window.routes?.cart_url || '/cart');
  }

  observeCustomilyRedirectOpen() {
    if (!window.MutationObserver) return;

    const observer = new MutationObserver(() => {
      if (!this.classList.contains('active')) return;
      if (!this.shouldRedirectToCartAfterCustomilyAdd()) return;

      this.redirectToCartAfterCustomilyAdd();
    });

    observer.observe(this, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  unlockProductMediaPosition() {
    const lock = this.lockedProductMedia;
    if (!lock?.element) return;

    if (lock.style === null) {
      lock.element.removeAttribute('style');
    } else {
      lock.element.setAttribute('style', lock.style);
    }

    if (lock.placeholder) {
      if (lock.placeholderStyle === null) {
        lock.placeholder.removeAttribute('style');
      } else {
        lock.placeholder.setAttribute('style', lock.placeholderStyle);
      }
    }

    this.lockedProductMedia = null;
  }

  open(triggeredBy) {
    if (this.shouldRedirectToCartAfterCustomilyAdd()) {
      this.redirectToCartAfterCustomilyAdd();
      return;
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    this.lockProductMediaPosition();
    document.documentElement.classList.add('cart-drawer-open');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.documentElement.classList.remove('cart-drawer-open');
    document.body.classList.remove('overflow-hidden');
    this.unlockProductMediaPosition();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (this.shouldRedirectToCartAfterCustomilyAdd()) {
      this.redirectToCartAfterCustomilyAdd();
      return;
    }

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
